import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Download, FileText, AlertCircle, X, Database, FileSpreadsheet } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { downloadEDI844Template, API_BASE_URL, getAuthHeaders } from '../config/api';
import axios from 'axios';

const EDI844CSVUpload = ({ onUploadComplete, onClose }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const queryClient = useQueryClient();

  const onDrop = useCallback((acceptedFiles) => {
    const validFiles = acceptedFiles.filter(file =>
      file.type === 'text/csv' || 
      file.type === 'text/plain' ||
      file.name.endsWith('.csv') ||
      file.name.endsWith('.txt') ||
      file.name.endsWith('.edi') ||
      file.name.endsWith('.844')
    );

    if (validFiles.length === 0) {
      toast.error('Please select a valid file (CSV, TXT, EDI, or 844)');
      return;
    }

    setFiles(validFiles);
    setUploadResult(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
      'application/vnd.ms-excel': ['.csv'],
      'text/edi': ['.edi', '.844']
    },
    multiple: false
  });

  const handleDownloadTemplate = async () => {
    try {
      await downloadEDI844Template();
      toast.success('EDI 844 template downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download template');
      console.error('Template download error:', error);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('description', `EDI 844 file upload: ${files[0].name}`);
      
      const result = await axios.post(`${API_BASE_URL}/api/edi/process-844`, formData, {
        headers: {
          'Authorization': getAuthHeaders().Authorization
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadResult(result.data);

      if (result.data.success) {
        toast.success(`EDI 844 file uploaded successfully! Batch: ${result.data.data.batchNumber}`);
        if (onUploadComplete) {
          onUploadComplete(result.data);
        }
        // Invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: ['chargebacks', 'batches'] });
      } else {
        toast.error('Upload completed with errors. Please check the results.');
      }

    } catch (error) {
      toast.error('Upload failed: ' + (error.message || 'Unknown error'));
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
    setUploadResult(null);
  };

  const resetUpload = () => {
    setFiles([]);
    setUploadProgress(0);
    setUploadResult(null);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">EDI 844 File Upload</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Close"
        >
          <X size={24} />
        </button>
      </div>

      {/* Template Download Section */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Download EDI 844 Template
            </h3>
            <p className="text-blue-700 text-sm">
              Use our template to ensure your file has the correct format for EDI 844 chargeback requests.
            </p>
          </div>
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title="Download template"
          >
            <Download size={20} className="mr-2" />
            Download Template
          </button>
        </div>
      </div>

      {/* File Upload Section */}
      <div className="mb-6">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input {...getInputProps()} />
          <FileSpreadsheet size={48} className="mx-auto text-gray-400 mb-4" />
          {isDragActive ? (
            <p className="text-blue-600 font-medium">Drop the CSV file here...</p>
          ) : (
            <div>
              <p className="text-gray-600 mb-2">
                Drag and drop an EDI 844 file here, or click to select
              </p>
              <p className="text-sm text-gray-500">
                Supported formats: CSV, TXT, EDI, 844 (max 10MB)
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Selected Files */}
      {files.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Selected Files</h3>
          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
            >
              <div className="flex items-center">
                <FileText size={20} className="text-gray-500 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                onClick={() => removeFile(index)}
                className="text-red-500 hover:text-red-700 transition-colors"
                title="Remove file"
              >
                <X size={20} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Processing EDI 844 file...</span>
            <span className="text-sm text-gray-500">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Upload Results */}
      {uploadResult && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Upload Results</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {uploadResult.data?.processedClaims || 0}
                </div>
                <div className="text-sm text-gray-600">Claims Processed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {uploadResult.data?.totalLines || 0}
                </div>
                <div className="text-sm text-gray-600">Total Lines</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {uploadResult.data?.totalAmount ? `$${uploadResult.data.totalAmount.toFixed(2)}` : '$0.00'}
                </div>
                <div className="text-sm text-gray-600">Total Amount</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {uploadResult.data?.errorCount || 0}
                </div>
                <div className="text-sm text-gray-600">Errors</div>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <div className="text-sm text-blue-800">
                <strong>Batch Number:</strong> {uploadResult.data?.batchNumber || 'N/A'}<br/>
                <strong>Status:</strong> {uploadResult.data?.status || 'N/A'}
              </div>
            </div>

            {uploadResult.data?.errorCount > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-900 mb-2">Processing Notes:</h4>
                <div className="text-sm text-gray-600">
                  {uploadResult.data.errorCount} records had errors during processing. 
                  This is normal when customers, contracts, or products are not found in the system.
                  Check the batch details for more information.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={resetUpload}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          title="Reset"
        >
          Reset
        </button>
        <button
          onClick={handleUpload}
          disabled={files.length === 0 || uploading}
          className={`px-6 py-2 rounded-lg transition-colors ${
            files.length === 0 || uploading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
          title="Upload EDI 844 file"
        >
          {uploading ? 'Processing...' : 'Upload EDI 844 File'}
        </button>
      </div>

      {/* Instructions */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">EDI 844 File Instructions:</h4>
        <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
          <li>Download the EDI 844 template above (CSV format)</li>
          <li>Fill in your chargeback claim data following the template format</li>
          <li>Save the file as CSV, TXT, EDI, or 844 format</li>
          <li>Upload the file using the form above</li>
          <li>Review the upload results for any errors</li>
        </ol>

        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <div className="flex items-start">
            <AlertCircle size={20} className="text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              <strong>Note:</strong> The system will automatically create chargeback claims and line items based on your file data.
              Products are matched by NDC or SKU, and contract pricing is automatically applied.
            </div>
          </div>
        </div>

        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
          <div className="flex items-start">
            <Database size={20} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <strong>Business Rules:</strong> Customer EDI ID and contract alias must exist in the system.
              Multiple line items can share the same claim number. Contract pricing is validated against sales prices.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EDI844CSVUpload;
